import { timeToMinutes, minutesToTime, getAvailableSlots, getDayName, toISODateString, parseISODate, isSameDay, wallClockIn, } from '../utils/date-utils';
import { dateKey, habitPausedOn, habitScheduledOn, scheduleSubject } from './habit-schedule';
import { deadlineDaysLeft } from './task-scope';
export class Scheduler {
    preferences;
    currentTime;
    constructor(config) {
        this.preferences = config.preferences;
        this.currentTime = config.currentTime ?? new Date();
    }
    /**
     * Generate schedule for a specific date
     */
    generateSchedule(options) {
        const { targetDate, tasks, existingSchedule, force = false, previouslyScheduledIds = new Set(), habitInstancesForDate = [], externalBusy = [] } = options;
        // Index instances by habit_id for O(1) lookup in the habit loop below.
        const instanceByHabit = new Map();
        for (const inst of habitInstancesForDate) {
            instanceByHabit.set(inst.habit_id, inst);
        }
        const targetDateObj = parseISODate(targetDate);
        console.log('[Scheduler] Generating schedule for:', targetDate);
        console.log('[Scheduler] Total tasks:', tasks.length, 'Previously scheduled:', previouslyScheduledIds.size);
        console.log('[Scheduler] Force:', force, 'Existing schedule:', !!existingSchedule);
        // Return existing schedule if not forcing regeneration
        if (existingSchedule && !force) {
            console.log('[Scheduler] Returning existing schedule for', targetDate, '(not regenerating - use force:true to regenerate)');
            return existingSchedule;
        }
        console.log('[Scheduler] REGENERATING schedule for', targetDate, '(force:', force, ')');
        // Filter tasks
        const habits = tasks.filter(t => t.is_habit &&
            !this.isHabitPaused(t, targetDateObj));
        console.log('[Scheduler] Habits (not paused):', habits.length, habits.map(h => h.title));
        const regularTasks = tasks.filter(t => !t.is_habit &&
            !this.isTaskPaused(t, targetDateObj) &&
            (t.status === 'todo' || t.status === 'in_progress'));
        console.log('[Scheduler] Regular tasks:', regularTasks.length);
        // DEBUG: Log ALL regular tasks with their pinned properties
        console.log('[Scheduler] All regular tasks with pinned info:', regularTasks.map(t => ({
            title: t.title,
            is_pinned: t.is_pinned,
            pin_type: t.pin_type,
            scheduled_for: t.scheduled_for,
            // Check the actual type of these values
            is_pinned_type: typeof t.is_pinned,
            pin_type_type: typeof t.pin_type,
        })));
        // DEBUG: Log hard-pinned tasks info
        const hardPinnedInRegular = regularTasks.filter(t => t.is_pinned && t.pin_type === 'hard' && t.scheduled_for);
        console.log('[Scheduler] Hard-pinned tasks in regular tasks:', hardPinnedInRegular.length, hardPinnedInRegular.map(t => ({ title: t.title, is_pinned: t.is_pinned, pin_type: t.pin_type, scheduled_for: t.scheduled_for })));
        // Build task map for dependency resolution
        const allTasksMap = new Map();
        for (const task of tasks) {
            allTasksMap.set(task.id, task);
        }
        // Work already finished today is history, not plan. It is deliberately
        // absent from `regularTasks` above (nothing left to schedule), but it must
        // survive into the new plan anyway: without its rows the timeline loses
        // the morning, and computeScheduleStats — which derives plan size from
        // scheduled_tasks — reports 0 of 3 where the day was really 5 of 8.
        // Rescheduling at 2pm should not delete the morning.
        const doneEntries = this.carryCompletedEntries(existingSchedule, allTasksMap, targetDateObj);
        return this.doGenerateSchedule(targetDateObj, habits, regularTasks, allTasksMap, previouslyScheduledIds, existingSchedule?.completed_tasks ?? [], instanceByHabit, externalBusy, doneEntries);
    }
    /**
     * The previous plan's rows for tasks that are now complete.
     *
     * Their slot is not re-blocked: finishing a 4pm task at 10am really does
     * free 4pm, and the point of pressing Reschedule is usually to fill the
     * time that just opened up. To keep that from producing two rows at the
     * same hour, a task whose planned slot is still ahead of us is re-anchored
     * to when it was actually completed — which is both truer and out of the
     * way of whatever gets scheduled into the slot it vacated.
     */
    carryCompletedEntries(existingSchedule, allTasksMap, targetDate) {
        if (!existingSchedule)
            return [];
        const completed = new Set(existingSchedule.completed_tasks ?? []);
        if (completed.size === 0)
            return [];
        const targetDateStr = toISODateString(targetDate);
        const entries = [];
        for (const entry of existingSchedule.scheduled_tasks ?? []) {
            if (entry.is_buffer || !completed.has(entry.task_id))
                continue;
            const task = allTasksMap.get(entry.task_id);
            // A habit is re-planned from its recurrence every time, so carrying the
            // old row would duplicate it.
            if (task?.is_habit)
                continue;
            let carried = entry;
            const endMinutes = timeToMinutes(entry.end_time);
            const nowMinutes = isSameDay(targetDate, this.currentTime)
                ? this.currentTime.getHours() * 60 + this.currentTime.getMinutes()
                : Number.POSITIVE_INFINITY;
            if (endMinutes > nowMinutes) {
                const at = this.completionMinutes(task, targetDateStr);
                if (at !== null) {
                    const duration = Math.max(1, entry.estimated_minutes || timeToMinutes(entry.end_time) - timeToMinutes(entry.start_time));
                    carried = {
                        ...entry,
                        start_time: minutesToTime(Math.max(0, at - duration)),
                        end_time: minutesToTime(at),
                        // Set HERE and nowhere else: re-anchoring is precisely the moment
                        // the planned slot was released to other work, which is what
                        // makes this row a receipt rather than part of the plan.
                        done_ahead: true,
                    };
                }
            }
            entries.push(carried);
        }
        return entries;
    }
    /** Local minute-of-day a task was completed, if it happened on this date. */
    completionMinutes(task, targetDateStr) {
        if (!task?.completed_at)
            return null;
        const instant = new Date(task.completed_at);
        if (Number.isNaN(instant.getTime()))
            return null;
        // On the user's wall clock: the server runs in UTC, where an evening
        // completion west of Greenwich lands on the next date.
        const at = wallClockIn(this.preferences.timezone, instant);
        if (toISODateString(at) !== targetDateStr)
            return null;
        return at.getHours() * 60 + at.getMinutes();
    }
    /**
     * Core scheduling logic
     */
    doGenerateSchedule(targetDate, habits, regularTasks, allTasksMap, previouslyScheduledIds, preserveCompletedTasks, instanceByHabit = new Map(), externalBusy = [], carriedCompleted = []) {
        const targetDateStr = toISODateString(targetDate);
        // DEBUG: Log the actual preference values being used
        console.log('[Scheduler] Preferences being used:', {
            min_duration: this.preferences.task_defaults?.min_duration,
            max_deep_work_stretch: this.preferences.scheduling_preferences?.max_deep_work_stretch,
            task_defaults: this.preferences.task_defaults,
            scheduling_preferences: this.preferences.scheduling_preferences,
        });
        // Get work hours for this day
        let { workStart, workEnd } = this.getWorkHoursForDate(targetDate);
        // Remembered before the bump below, because "does this day cross
        // midnight?" is a fact about the CONFIGURED hours, not about how much of
        // them is left.
        const configuredStart = timeToMinutes(workStart);
        const configuredEnd = timeToMinutes(workEnd);
        const crossesMidnight = configuredEnd <= configuredStart;
        /** Set when today's window is simply over — see below. */
        let dayIsOver = false;
        // If scheduling today and current time is past work start, adjust
        if (isSameDay(targetDate, this.currentTime)) {
            const currentMinutes = this.currentTime.getHours() * 60 + this.currentTime.getMinutes();
            if (currentMinutes > configuredStart) {
                // Round up to next 15-minute interval
                const roundedMinutes = Math.ceil(currentMinutes / 15) * 15;
                workStart = minutesToTime(roundedMinutes);
            }
            // Pushing the start past the end turns an ordinary 08:00–22:00 day into
            // something that LOOKS overnight (end <= start), and getAvailableSlots
            // then invents a window from the new start to midnight. That booked
            // work at 23:00 for someone who had said they stop at 22:00. A normal
            // day whose end has passed simply has no room left in it.
            if (!crossesMidnight && timeToMinutes(workStart) >= configuredEnd) {
                dayIsOver = true;
            }
        }
        // Build blocked slots from habits and hard-pinned tasks
        const blockedSlots = [];
        const fixedScheduledTasks = [];
        const flexibleHabits = [];
        const habitCandidates = [];
        const pinnedCandidates = [];
        const hardPinnedTaskIds = new Set();
        // External calendar commitments reserve their time so the scheduler
        // doesn't hand it to flexible work. That is the ONLY authority they have
        // — see scheduledHabitRanges below for why they don't displace habits or
        // hard pins.
        for (const busy of externalBusy) {
            blockedSlots.push([busy.start, busy.end]);
        }
        // Collect hard-pinned tasks scheduled for this date
        for (const task of regularTasks) {
            if (task.is_pinned && task.pin_type === 'hard' && task.scheduled_for) {
                // Check if scheduled_for date matches target date
                const scheduledDate = task.scheduled_for.split('T')[0];
                if (scheduledDate === targetDateStr) {
                    // Extract time from scheduled_for (format: "2025-01-15T10:00:00" or "2025-01-15T10:00")
                    const timePart = task.scheduled_for.split('T')[1];
                    if (timePart) {
                        const startTime = timePart.substring(0, 5); // "HH:MM"
                        const startMinutes = timeToMinutes(startTime);
                        const duration = task.estimated_minutes;
                        const endMinutes = startMinutes + duration;
                        const endTime = minutesToTime(endMinutes);
                        pinnedCandidates.push({
                            task,
                            startTime,
                            endTime,
                            startMinutes,
                            endMinutes,
                            duration,
                        });
                        hardPinnedTaskIds.add(task.id);
                    }
                }
            }
        }
        console.log('[Scheduler] Hard-pinned tasks:', pinnedCandidates.length, pinnedCandidates.map(p => p.task.title));
        // Collect habit time slots
        for (const habit of habits) {
            const occursToday = this.habitOccursOnDate(habit, targetDate);
            console.log('[Scheduler] Habit', habit.title, 'occurs on', getDayName(targetDate), '?', occursToday, 'recurrence:', habit.recurrence);
            if (occursToday) {
                // Prefer frozen snapshot from a completed instance on this date,
                // so historical timings survive edits to habit.recurrence.
                const inst = instanceByHabit.get(habit.id);
                const habitTime = (inst && inst.start_time && inst.estimated_minutes)
                    ? [inst.start_time, inst.estimated_minutes]
                    : this.getHabitTime(habit);
                if (habitTime) {
                    const [startTime, duration] = habitTime;
                    const startMinutes = timeToMinutes(startTime);
                    const endMinutes = startMinutes + duration;
                    const endTime = minutesToTime(endMinutes);
                    habitCandidates.push({
                        habit,
                        startTime,
                        endTime,
                        startMinutes,
                        endMinutes,
                        duration,
                    });
                }
                else {
                    // Habit without time_of_day - schedule flexibly
                    flexibleHabits.push(habit);
                }
            }
        }
        // Sort by start time, then by priority
        habitCandidates.sort((a, b) => {
            if (a.startMinutes !== b.startMinutes) {
                return a.startMinutes - b.startMinutes;
            }
            return a.habit.priority - b.habit.priority;
        });
        // Detect and resolve overlaps among OUR OWN fixed items only.
        //
        // External calendar events are deliberately NOT seeded here. A meeting
        // blocks the flexible work we place automatically, but it does not
        // outrank things you put on the day yourself: habits keep their time and
        // hard-pinned tasks keep theirs, even when a meeting lands on top. An
        // earlier version dropped them, which meant a 9am invite could silently
        // delete your gym habit with no trace — the calendar quietly winning
        // against your own explicit choices. Overlaps are surfaced in the UI
        // instead, so you decide which one gives way.
        const scheduledHabitRanges = [];
        for (const hc of habitCandidates) {
            let hasOverlap = false;
            for (const [scheduledStart, scheduledEnd] of scheduledHabitRanges) {
                if (hc.startMinutes < scheduledEnd && hc.endMinutes > scheduledStart) {
                    hasOverlap = true;
                    break;
                }
            }
            if (hasOverlap) {
                continue; // Skip overlapping habit
            }
            blockedSlots.push([hc.startTime, hc.endTime]);
            fixedScheduledTasks.push({
                task_id: hc.habit.id,
                start_time: hc.startTime,
                end_time: hc.endTime,
                estimated_minutes: hc.duration,
                is_buffer: false,
                auto_scheduled: true,
            });
            scheduledHabitRanges.push([hc.startMinutes, hc.endMinutes, hc.habit.title]);
        }
        // Sort hard-pinned tasks by start time
        pinnedCandidates.sort((a, b) => a.startMinutes - b.startMinutes);
        // Add hard-pinned tasks to blocked slots (they take priority, similar to habits)
        for (const pc of pinnedCandidates) {
            // Check for overlap with already scheduled ranges
            let hasOverlap = false;
            for (const [scheduledStart, scheduledEnd] of scheduledHabitRanges) {
                if (pc.startMinutes < scheduledEnd && pc.endMinutes > scheduledStart) {
                    hasOverlap = true;
                    console.log('[Scheduler] Hard-pinned task', pc.task.title, 'overlaps with existing slot');
                    break;
                }
            }
            if (!hasOverlap) {
                blockedSlots.push([pc.startTime, pc.endTime]);
                fixedScheduledTasks.push({
                    task_id: pc.task.id,
                    start_time: pc.startTime,
                    end_time: pc.endTime,
                    estimated_minutes: pc.duration,
                    is_buffer: false,
                    auto_scheduled: false, // Not auto-scheduled, user pinned it
                });
                scheduledHabitRanges.push([pc.startMinutes, pc.endMinutes, pc.task.title]);
            }
        }
        // Get available slots after blocking habits and hard-pinned tasks
        let availableSlots = dayIsOver ? [] : getAvailableSlots(workStart, workEnd, blockedSlots, this.preferences.task_defaults?.min_duration ?? 15);
        // For today's schedule, filter out time slots that have already passed
        if (isSameDay(targetDate, this.currentTime)) {
            const currentMinutes = this.currentTime.getHours() * 60 + this.currentTime.getMinutes();
            const workEndMinutes = timeToMinutes(workEnd);
            const workStartMinutes = timeToMinutes(workStart);
            // Check if this is an overnight schedule (workEnd <= original workStart)
            // Note: workStart may have been adjusted to current time, so we check workEnd vs current
            const isOvernightSchedule = workEndMinutes < 720 && workStartMinutes >= 720;
            const minSlot = this.preferences.task_defaults?.min_duration ?? 15;
            // Same rounding as the workStart adjustment above, so a day that starts
            // "now" starts on the same 15-minute boundary either way.
            const roundedNow = Math.ceil(currentMinutes / 15) * 15;
            availableSlots = availableSlots.flatMap(([slotStart, slotEnd]) => {
                const keep = (() => {
                    if (isOvernightSchedule) {
                        // Period 1 is the evening (start >= workStart); Period 2 is the
                        // small hours of the SAME calendar day (end <= workEnd).
                        const isPeriod2Slot = slotEnd <= workEndMinutes && slotStart < 720;
                        if (currentMinutes >= workStartMinutes) {
                            // Evening: today's small hours are behind us. Tonight's early
                            // morning belongs to tomorrow's schedule, not this one.
                            return !isPeriod2Slot;
                        }
                        if (currentMinutes < workEndMinutes) {
                            // Mid-shift, in the small hours. What's left of tonight is
                            // available — and so is this evening, which is still ahead.
                            // Dropping Period 1 here threw away the larger half of an
                            // overnight worker's day.
                            return isPeriod2Slot ? slotEnd > currentMinutes : true;
                        }
                    }
                    // Normal schedule, or the gap between shifts.
                    return slotEnd > currentMinutes;
                })();
                if (!keep)
                    return [];
                // A slot already under way starts NOW, not at its nominal start.
                // Without this, rescheduling at 2am filled 00:00–02:00 — hours that
                // had already gone — because only the slot's END was checked.
                const start = slotStart < roundedNow && slotEnd > currentMinutes ? roundedNow : slotStart;
                if (slotEnd - start < minSlot)
                    return [];
                return [[start, slotEnd]];
            });
        }
        // Schedule flexible habits first, then regular tasks
        // IMPORTANT: Exclude ALL hard-pinned tasks from flexible scheduling, not just those for today
        // Hard-pinned tasks should ONLY appear on their scheduled date at their scheduled time
        // Also exclude tasks below minimum duration or above maximum duration setting
        const minDuration = this.preferences.task_defaults?.min_duration ?? 15;
        const maxDuration = this.preferences.scheduling_preferences?.max_deep_work_stretch ?? 120;
        const flexibleRegularTasks = regularTasks.filter(t => !(t.is_pinned && t.pin_type === 'hard' && t.scheduled_for) &&
            t.estimated_minutes >= minDuration &&
            t.estimated_minutes <= maxDuration);
        // DEBUG: Log filtering results
        const excludedHardPinned = regularTasks.filter(t => t.is_pinned && t.pin_type === 'hard' && t.scheduled_for);
        const excludedTooShort = regularTasks.filter(t => t.estimated_minutes < minDuration);
        const excludedTooLong = regularTasks.filter(t => t.estimated_minutes > maxDuration);
        console.log('[Scheduler] Filtering for', targetDateStr, '- minDuration:', minDuration, 'maxDuration:', maxDuration);
        console.log('[Scheduler] Filtering for', targetDateStr, '- Excluding hard-pinned:', excludedHardPinned.length, excludedHardPinned.map(t => t.title));
        console.log('[Scheduler] Filtering for', targetDateStr, '- Excluding too short (<' + minDuration + 'min):', excludedTooShort.length, excludedTooShort.map(t => `${t.title} (${t.estimated_minutes}min)`));
        console.log('[Scheduler] Filtering for', targetDateStr, '- Excluding too long (>' + maxDuration + 'min):', excludedTooLong.length, excludedTooLong.map(t => `${t.title} (${t.estimated_minutes}min)`));
        console.log('[Scheduler] Flexible regular tasks to schedule:', flexibleRegularTasks.length, flexibleRegularTasks.map(t => t.title));
        // Note: Habits are NOT filtered by minimum duration - they can be short (e.g., 5-min daily check-in)
        // Only regular tasks are filtered by min_duration
        const tasksToSchedule = [...flexibleHabits, ...flexibleRegularTasks];
        const scheduledTasks = this.scheduleTasksInSlots(tasksToSchedule, availableSlots, targetDate, allTasksMap, previouslyScheduledIds);
        // Combine and sort by time. Completed rows carried over from the previous
        // plan go in last so a task that is somehow both scheduled and carried
        // keeps its freshly-scheduled row rather than gaining a duplicate.
        const placedIds = new Set([...fixedScheduledTasks, ...scheduledTasks].filter(t => !t.is_buffer).map(t => t.task_id));
        const allScheduled = [
            ...fixedScheduledTasks,
            ...scheduledTasks,
            ...carriedCompleted.filter(t => !placedIds.has(t.task_id)),
        ];
        allScheduled.sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
        // Calculate summary
        const totalMinutes = allScheduled
            .filter(t => !t.is_buffer)
            .reduce((sum, t) => sum + t.estimated_minutes, 0);
        // Count tasks (excluding buffers/breaks)
        const allTaskIds = allScheduled.filter(t => !t.is_buffer).map(t => t.task_id);
        const completedTaskIds = new Set(preserveCompletedTasks);
        const tasksCompleted = allTaskIds.filter(id => completedTaskIds.has(id)).length;
        const tasksRemaining = allTaskIds.length - tasksCompleted;
        const completionRate = allTaskIds.length > 0 ? (tasksCompleted / allTaskIds.length) * 100 : 0;
        const schedule = {
            date: targetDateStr,
            user_id: this.preferences.user_id,
            constraints: {
                // The hours the user CONFIGURED, not the start we bumped to "now".
                // This record syncs, and "work_start: 23:30, work_end: 22:00" is
                // nonsense to anything reading it back — including the code that
                // explains why a plan came out empty.
                work_start: minutesToTime(configuredStart),
                work_end: workEnd,
                // External busy ranges keep their calendar-name label; everything
                // else in blockedSlots came from a habit or a hard pin.
                blocked_slots: blockedSlots.map(([start, end]) => ({
                    start,
                    end,
                    label: externalBusy.find((b) => b.start === start && b.end === end)?.label ?? 'Habit',
                })),
                preferences: this.preferences.scheduling_preferences,
            },
            scheduled_tasks: allScheduled,
            completed_tasks: preserveCompletedTasks,
            summary: {
                total_scheduled_minutes: totalMinutes,
                total_completed_minutes: 0,
                completion_rate: completionRate,
                tasks_completed: tasksCompleted,
                tasks_remaining: tasksRemaining,
                tasks_behind_schedule: 0,
            },
            generated_at: new Date().toISOString(),
            last_rescheduled_at: null,
        };
        return schedule;
    }
    /**
     * Schedule tasks into available slots
     */
    scheduleTasksInSlots(tasks, availableSlots, targetDate, allTasksMap, previouslyScheduledIds) {
        const scheduledTasks = [];
        const scheduledTaskIds = new Set();
        const maxDeepWork = this.preferences.scheduling_preferences?.max_deep_work_stretch ?? 120;
        const energyBonus = this.preferences.scheduling_preferences?.energy_match_bonus ?? 0.15;
        for (const [slotStart, slotEnd] of availableSlots) {
            let currentTime = slotStart;
            let continuousWork = 0;
            while (currentTime < slotEnd) {
                // Get available tasks (not yet scheduled today AND not scheduled on previous days)
                // Note: Habits (is_habit=true) can repeat daily, so don't exclude them
                const availableTasks = tasks.filter(t => !scheduledTaskIds.has(t.id) &&
                    (t.is_habit || !previouslyScheduledIds.has(t.id)));
                if (availableTasks.length === 0)
                    break;
                // Score tasks for this time slot
                const scoredTasks = this.scoreTasksForTime(availableTasks, targetDate, currentTime, energyBonus);
                let taskScheduled = false;
                for (const { task, score } of scoredTasks) {
                    const taskDuration = task.estimated_minutes;
                    const remaining = slotEnd - currentTime;
                    if (taskDuration > remaining)
                        continue; // Doesn't fit
                    // Check if we need a break
                    if (continuousWork > 0 && continuousWork + taskDuration > maxDeepWork) {
                        const breakDuration = 15;
                        if (currentTime + breakDuration <= slotEnd) {
                            scheduledTasks.push({
                                task_id: '',
                                start_time: minutesToTime(currentTime),
                                end_time: minutesToTime(currentTime + breakDuration),
                                estimated_minutes: breakDuration,
                                is_buffer: true,
                                auto_scheduled: true,
                            });
                            currentTime += breakDuration;
                            continuousWork = 0;
                            if (taskDuration > slotEnd - currentTime)
                                continue;
                        }
                    }
                    // Check dependencies
                    if (!this.dependenciesMet(task, scheduledTasks, allTasksMap, previouslyScheduledIds)) {
                        continue;
                    }
                    // Schedule the task
                    const taskEnd = currentTime + taskDuration;
                    scheduledTasks.push({
                        task_id: task.id,
                        start_time: minutesToTime(currentTime),
                        end_time: minutesToTime(taskEnd),
                        estimated_minutes: taskDuration,
                        is_buffer: false,
                        auto_scheduled: true,
                    });
                    scheduledTaskIds.add(task.id);
                    currentTime = taskEnd;
                    continuousWork += taskDuration;
                    taskScheduled = true;
                    // Force break if needed
                    if (continuousWork >= maxDeepWork) {
                        const breakDuration = 15;
                        if (currentTime + breakDuration <= slotEnd) {
                            scheduledTasks.push({
                                task_id: '',
                                start_time: minutesToTime(currentTime),
                                end_time: minutesToTime(currentTime + breakDuration),
                                estimated_minutes: breakDuration,
                                is_buffer: true,
                                auto_scheduled: true,
                            });
                            currentTime += breakDuration;
                            continuousWork = 0;
                        }
                    }
                    break;
                }
                if (!taskScheduled)
                    break;
            }
        }
        return scheduledTasks;
    }
    /**
     * Score tasks for a specific time slot with energy matching
     */
    scoreTasksForTime(tasks, targetDate, timeMinutes, energyBonus) {
        const currentEnergy = this.getEnergyLevelAtTime(targetDate, timeMinutes);
        const scored = [];
        const targetDateStr = toISODateString(targetDate);
        for (const task of tasks) {
            let score = 0;
            // Priority (35%)
            score += (6 - task.priority) * 0.35;
            // Deadline urgency (35%)
            const daysUntil = deadlineDaysLeft(task, targetDateStr, this.preferences.timezone);
            if (daysUntil !== null) {
                if (daysUntil < 0) {
                    score += 1.0 * 0.35; // Overdue
                }
                else if (daysUntil === 0) {
                    score += 0.95 * 0.35; // Due today
                }
                else if (daysUntil <= 3) {
                    score += 0.7 * 0.35; // Due soon
                }
                else {
                    score += 0.3 * 0.35; // Future
                }
            }
            else {
                score += 0.2 * 0.35; // No deadline
            }
            // Energy matching (15%)
            if (currentEnergy && this.preferences.scheduling_preferences.respect_energy_patterns) {
                const taskEnergy = task.energy_level || 'medium';
                if (taskEnergy === currentEnergy) {
                    score += energyBonus;
                }
                else if ((currentEnergy === 'high' && taskEnergy === 'medium') ||
                    (currentEnergy === 'medium' && (taskEnergy === 'high' || taskEnergy === 'low')) ||
                    (currentEnergy === 'low' && taskEnergy === 'medium')) {
                    score += energyBonus * 0.5;
                }
            }
            // Duration (15%) - prefer shorter tasks
            if (task.estimated_minutes <= 30) {
                score += 0.15;
            }
            else if (task.estimated_minutes <= 90) {
                score += 0.10;
            }
            else {
                score += 0.05;
            }
            scored.push({ task, score });
        }
        // Sort by score descending
        scored.sort((a, b) => b.score - a.score);
        return scored;
    }
    /**
     * Check if all dependencies are met
     */
    dependenciesMet(task, scheduledTasks, allTasksMap, previouslyScheduledIds) {
        if (!task.dependencies || task.dependencies.length === 0) {
            return true;
        }
        const scheduledIds = new Set([
            ...scheduledTasks.map(st => st.task_id),
            ...previouslyScheduledIds,
        ]);
        for (const depId of task.dependencies) {
            if (scheduledIds.has(depId))
                continue;
            // Check if dependency is completed or doesn't exist in active tasks
            // (if not in map, it means it was filtered out - likely completed)
            const depTask = allTasksMap.get(depId);
            if (!depTask || depTask.status === 'completed')
                continue;
            // Dependency not met
            return false;
        }
        return true;
    }
    /**
     * Check if habit is paused for the given date
     */
    isHabitPaused(habit, targetDate) {
        return habitPausedOn(scheduleSubject(habit), dateKey(targetDate));
    }
    /**
     * Check if task is paused for the given date
     */
    isTaskPaused(task, targetDate) {
        // The same date-key rule as habits and every other surface.
        return habitPausedOn(task, dateKey(targetDate));
    }
    /**
     * Check if habit occurs on the given date.
     *
     * Delegates to `habitScheduledOn` so the planner, the sidebar widget and the
     * streak all answer this question the same way. The local version handled
     * only daily and weekly and fell through to `false`, which meant a Monthly
     * habit — an option the recurrence picker offers — was never once put on a
     * plan.
     */
    habitOccursOnDate(habit, targetDate) {
        return habitScheduledOn(scheduleSubject(habit), dateKey(targetDate));
    }
    /**
     * Get habit scheduled time
     */
    getHabitTime(habit) {
        if (!habit.recurrence || !habit.recurrence.time_of_day) {
            return null;
        }
        return [habit.recurrence.time_of_day, habit.estimated_minutes];
    }
    /**
     * Get work hours for a specific date
     */
    getWorkHoursForDate(targetDate) {
        const dayName = getDayName(targetDate);
        const dayHours = this.preferences.work_hours[dayName] ?? { start: '08:00', end: '22:00' };
        return { workStart: dayHours.start, workEnd: dayHours.end };
    }
    /**
     * Get energy level at a specific time (handles overnight blocks)
     */
    getEnergyLevelAtTime(targetDate, timeMinutes) {
        const dayName = getDayName(targetDate);
        const patterns = this.preferences.energy_patterns[dayName] ?? [];
        for (const block of patterns) {
            const blockStart = timeToMinutes(block.start);
            const blockEnd = timeToMinutes(block.end);
            // Handle overnight blocks (e.g., 21:00-06:00)
            if (blockEnd <= blockStart) {
                // Block spans midnight
                if (timeMinutes >= blockStart || timeMinutes < blockEnd) {
                    return block.level;
                }
            }
            else {
                // Normal block
                if (timeMinutes >= blockStart && timeMinutes < blockEnd) {
                    return block.level;
                }
            }
        }
        return null;
    }
    /**
     * Reschedule incomplete tasks
     */
    reschedule(options) {
        return this.generateSchedule({ ...options, force: true });
    }
}
//# sourceMappingURL=scheduler.js.map